import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { provideRouter } from '@angular/router';
import { environment } from '../environments/environment';
import { SOFTLINE_CONFIG_AUTHENTICATION_FORWARD_ROUTE } from '@softline/auth';
import {
  provideDarkTheme,
  remoteConfigProviders,
  SOFTLINE_CONFIG_DEFAULT_THEME,
  SOFTLINE_CONFIG_THEMES,
} from '@softline/application';
import {
  accountContextProviders,
  contextSettingsProviders,
  settingsProviders,
  userContextProviders,
  utilitiesProviders,
} from '@softapps/allgemein/utils';
import { passwordProviders } from '@softapps/allgemein/password';
import { loginProviders, withLogin } from '@softapps/allgemein/login';
import { moduleProviders } from '@softapps/allgemein/modules';
import { wwsCoreProviders } from '@softapps/wws/core';
import { bestellvorschlagProviders } from '@softapps/mde/bestellvorschlag';
import { bestellerfassungProviders } from '@softapps/mde/bestellerfassung';
import { etikettendruckProviders } from '@softapps/mde/etikettendruck';
import { warenverprobungProviders } from '@softapps/mde/warenverprobung';
import { retourenProviders } from '@softapps/mde/retouren';
import { artikelInfoProviders } from '@softapps/wws/artikel-info';
import { platform } from '../platforms/platform';
import { materiallieferscheinScanProviders } from '@softapps/mde/materiallieferschein-scan';
import {
  provideSoftApps,
  SOFTLINE_ROUTES_APPS,
  SOFTLINE_ROUTES_PUBLIC,
  SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING,
} from '@softapps/core';
import { energietransportProviders } from '@softapps/wws/energietransport';
import { eslLinkProviders } from '@softapps/handel/esl-link';
import { provideDeliveryNoteProcessingModule } from '@softapps/wws/delivery-note-processing';
import { gesamtabfrageProviders } from '@softapps/wws/gesamtabfrage';
import { publicRoutes, shellRoutes } from './app.routes';
import { provideStorageLocation } from '@softapps/mde/storage-location';
import { provideMdeInventoryModule } from '../../../../../../libs/softapps/mde/inventory/src/lib/inventory.providers';
import { provideConstructionPartsModule } from '@softapps/gewerbe/construction-parts';
import { provideEslConfigModule } from '@softapps/handel/esl-config';
import {
  provideEslLinkStorageProviders,
  provideEslSearchProviders,
} from '@softapps/handel/esl-link-storage';
import { lieferscheinProviders } from '@softapps/wws/lieferschein';
import { stempelerfassungProviders } from '@softapps/pms/stempelerfassung';
import {
  arbeitsberichtProviders,
  SOFTLINE_CONFIG_ARBEITSBERICHT,
  SOFTLINE_DEFINITION_ARBEITSBERICHT_EDIT,
  SOFTLINE_DEFINITION_ARBEITSBERICHT_INPUT,
  SOFTLINE_DEFINITION_ARBEITSBERICHT_LIST,
  SOFTLINE_DEFINITION_ARBEITSBERICHT_QUERY,
} from '@softapps/gewerbe/arbeitsbericht';
import { SOFTLINE_CONFIG_DEFINITION } from '@softline/dynamic/base';
import { default as arbeitsberichtEditDefinition } from './../definitions/arbeitsbericht/edit.json';
import { default as arbeitsberichtInputDefinition } from './../definitions/arbeitsbericht/input.json';
import { default as arbeitsberichtListDefinition } from './../definitions/arbeitsbericht/list.json';
import { default as arbeitsberichtQueryDefinition } from './../definitions/arbeitsbericht/query.json';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([]),
    provideSoftApps({ connection: environment.connection }, withLogin()),

    { provide: SOFTLINE_CONFIG_DEFAULT_THEME, useValue: 'lagerhaus' },
    provideDarkTheme(false),
    {
      provide: SOFTLINE_CONFIG_THEMES,
      useValue: {
        key: 'lagerhaus',
        name: 'Lagerhaus',
      },
      multi: true,
    },

    { provide: SOFTLINE_ROUTES_APPS, useValue: shellRoutes, multi: true },
    { provide: SOFTLINE_ROUTES_PUBLIC, useValue: publicRoutes, multi: true },
    { provide: SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING, useValue: true },

    /*
    importProvidersFrom(
      BrowserAnimationsModule,
      RouterModule.forRoot(routes, { initialNavigation: 'enabledBlocking' }),

      // Framework
      CoreModule.forRoot(),
      StoreModule.forRoot(),
      ConnectionModule.forRoot(environment.connection),
      AuthenticationModule.forRoot(),
      JwtAuthenticationModule.forRoot(),
      ApplicationModule.forRoot(),
      AppearanceModule.forRoot(),
      DynamicModule.forRoot(),
      MasterDataModule.forRoot(),
      DeveloperModule.forRoot(),
      ConsoleModule.forRoot(),
      ItemScanModule.forRoot(),
      OfflineModule.forRoot(),
      SoftappsCoreModule.forRoot()
    ),*/

    // Allgemein
    ...utilitiesProviders,
    ...contextSettingsProviders,
    ...userContextProviders,
    ...accountContextProviders,

    ...settingsProviders,
    ...remoteConfigProviders,
    ...passwordProviders,
    ...loginProviders,
    ...moduleProviders,

    //Wws
    // ...gesamtabfrageProviders,
    // ...stempelerfassungProviders,

    // MDE
    ...wwsCoreProviders,

    ...bestellvorschlagProviders,
    ...bestellerfassungProviders,
    ...etikettendruckProviders,
    ...provideMdeInventoryModule(),
    ...warenverprobungProviders,
    ...retourenProviders,
    ...materiallieferscheinScanProviders,
    provideEslConfigModule(),
    provideEslLinkStorageProviders({ storageSelection: false }),
    provideEslSearchProviders(),
    ...provideStorageLocation(),

    ...lieferscheinProviders,
    ...stempelerfassungProviders,
    ...arbeitsberichtProviders,

    ...artikelInfoProviders,

    ...energietransportProviders,
    ...gesamtabfrageProviders,
    ...provideDeliveryNoteProcessingModule(),

    provideConstructionPartsModule(),

    {
      provide: SOFTLINE_CONFIG_ARBEITSBERICHT,
      useValue: {
        clickAction: 'navigate',
        useSecondaryBorderStyle: true,
        hideEntryInDetails: true,
        allowDelete: true,
        actions: [
          {
            label: 'Auftrag Scannen',
            icon: 'fas fa-barcode',
            actionType: 'scanOrder',
          },
          {
            label: 'Letzte Objekte',
            icon: 'fas fa-history',
            actionType: 'recentObjects',
          },
        ],
      },
    },
    {
      provide: SOFTLINE_CONFIG_DEFINITION,
      useValue: {
        name: SOFTLINE_DEFINITION_ARBEITSBERICHT_LIST,
        definition: arbeitsberichtListDefinition,
        priority: 1,
      },
      multi: true,
    },
    {
      provide: SOFTLINE_CONFIG_DEFINITION,
      useValue: {
        name: SOFTLINE_DEFINITION_ARBEITSBERICHT_INPUT,
        definition: arbeitsberichtInputDefinition,
        priority: 1,
      },
      multi: true,
    },
    {
      provide: SOFTLINE_CONFIG_DEFINITION,
      useValue: {
        name: SOFTLINE_DEFINITION_ARBEITSBERICHT_EDIT,
        definition: arbeitsberichtEditDefinition,
        priority: 1,
      },
      multi: true,
    },
    {
      provide: SOFTLINE_CONFIG_DEFINITION,
      useValue: {
        name: SOFTLINE_DEFINITION_ARBEITSBERICHT_QUERY,
        definition: arbeitsberichtQueryDefinition,
        priority: 1,
      },
      multi: true,
    },

    ...platform.providers,
    { provide: LOCALE_ID, useValue: 'de' },
    {
      provide: SOFTLINE_CONFIG_AUTHENTICATION_FORWARD_ROUTE,
      useValue: '/',
    },
  ],
};
